import React from "react";
import { NavLink, useNavigate, Navigate, Outlet } from "react-router-dom";
import { LayoutDashboard, Inbox, Rocket, FileText, BookMarked, LogOut, ExternalLink } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { PLATFORM_URL } from "@/lib/api";

const links = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/contacts", label: "Contact Enquiries", icon: Inbox },
  { to: "/admin/beta", label: "Private Beta", icon: Rocket },
  { to: "/admin/blog", label: "Blog CMS", icon: FileText },
  { to: "/admin/publications", label: "Publications", icon: BookMarked },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  if (user === null)
    return <div className="flex min-h-screen items-center justify-center bg-navy text-silver/50">Loading…</div>;
  if (user === false) return <Navigate to="/admin/login" replace />;

  const doLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="flex min-h-screen bg-navy">
      <aside className="fixed inset-y-0 left-0 hidden w-64 flex-col border-r border-navy-border bg-navy-surface lg:flex">
        <div className="border-b border-navy-border p-5"><Logo /></div>
        <nav className="flex-1 space-y-1 p-4">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`admin-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-astro-blue text-white" : "text-silver/70 hover:bg-navy hover:text-white"}`
              }
            >
              <l.icon className="h-4.5 w-4.5" /> {l.label}
            </NavLink>
          ))}
          <a href={PLATFORM_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-silver/70 hover:bg-navy hover:text-white transition-colors">
            <ExternalLink className="h-4.5 w-4.5" /> Governance OS
          </a>
        </nav>
        <div className="border-t border-navy-border p-4">
          <p className="mb-3 truncate px-1 text-xs text-silver/50">{user.email}</p>
          <button onClick={doLogout} data-testid="admin-logout" className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-silver/70 hover:bg-navy hover:text-white transition-colors">
            <LogOut className="h-4.5 w-4.5" /> Sign out
          </button>
        </div>
      </aside>

      <div className="flex-1 lg:ml-64">
        {/* Mobile top nav */}
        <div className="flex items-center justify-between gap-3 overflow-x-auto border-b border-navy-border bg-navy-surface p-4 lg:hidden">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} className={({ isActive }) => `whitespace-nowrap text-sm ${isActive ? "text-astro-light" : "text-silver/60"}`}>{l.label}</NavLink>
          ))}
          <button onClick={doLogout} className="text-sm text-silver/60">Exit</button>
        </div>
        <div className="p-6 lg:p-10">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
